import { useState } from 'react';
import { notifier } from 'components/Toaster';
import useManageRefund from 'hooks/API/useManageRefund';

/** Rejection reasons shown in the dropdown */
const REJECTION_REASONS = [
  { value: 'outside_refund_window', label: 'Outside refund window' },
  { value: 'exceeded_consumption_limit', label: 'Exceeded consumption limit' },
  { value: 'duplicate_request', label: 'Duplicate request' },
  { value: 'policy_violation', label: 'Policy violation' },
  { value: 'insufficient_evidence', label: 'Insufficient evidence' },
  { value: 'other', label: 'Other' },
];

function RefundActionModal({ mode, refund, onClose, onSuccess }) {
  const { mutateAsync, isLoading } = useManageRefund();

  const [note, setNote] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [error, setError] = useState('');

  if (!refund) return null;

  const isApprove = mode === 'approve';
  const title = isApprove ? 'Approve & Process Refund' : 'Reject Refund';
  const confirmBtnClass = isApprove ? 'btn btn-success' : 'btn btn-danger';
  const confirmBtnLabel = isApprove ? 'Confirm & Process Now' : 'Confirm Rejection';

  const handleSubmit = async () => {
    setError('');

    if (!isApprove && !rejectionReason) {
      setError('Please select a rejection reason.');
      return;
    }

    try {
      if (isApprove) {
        await mutateAsync({
          action: 'approve',
          refundId: refund._id,
          note: note.trim() || undefined,
        });
        notifier.success('Refund approved — amount automatically refunded via payment gateway');
      } else {
        await mutateAsync({
          action: 'reject',
          refundId: refund._id,
          rejectionReason,
          note: note.trim() || undefined,
        });
        notifier.success('Refund rejected');
      }

      onSuccess();
      onClose();
    } catch (err) {
      const msg =
        err?.response?.data?.message || err?.message || 'Something went wrong. Please try again.';
      setError(msg);
    }
  };

  return (
    /* Backdrop */
    <div
      className="modal d-block"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          {/* Header */}
          <div className="modal-header">
            <h5 className="modal-title fw-semibold">{title}</h5>
            <button type="button" className="btn-close" onClick={onClose} disabled={isLoading} />
          </div>

          {/* Body */}
          <div className="modal-body">
            {/* Course + amount summary */}
            <div className="p-3 mb-3 rounded" style={{ background: '#f8f9fa' }}>
              <p className="mb-1 fw-semibold">{refund.courseName}</p>
              <p className="mb-0 text-muted">
                Amount:{' '}
                <span className="fw-bold text-dark">
                  {refund.currency === 'INR' ? '₹' : refund.currency}
                  {Number(refund.amount).toLocaleString('en-IN')}
                </span>
              </p>
            </div>

            {/* Auto-process notice for approval */}
            {isApprove && (
              <div className="alert alert-success py-2 mb-3" style={{ fontSize: '0.83rem' }}>
                ⚡ This will <strong>immediately refund</strong> the amount to the user's original
                payment method via the payment gateway. No further action is needed.
              </div>
            )}

            {/* Rejection reason dropdown */}
            {!isApprove && (
              <div className="mb-3">
                <label className="form-label fw-semibold">
                  Rejection Reason <span className="text-danger">*</span>
                </label>
                <select
                  className="form-select"
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  disabled={isLoading}
                >
                  <option value="">— Select a reason —</option>
                  {REJECTION_REASONS.map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Admin note */}
            <div className="mb-3">
              <label className="form-label fw-semibold">
                Admin Note <span className="text-muted fw-normal">(optional)</span>
              </label>
              <textarea
                className="form-control"
                rows={3}
                placeholder={
                  isApprove
                    ? 'Add any internal notes for this approval…'
                    : 'Provide additional context for the rejection…'
                }
                value={note}
                onChange={(e) => setNote(e.target.value)}
                maxLength={500}
                disabled={isLoading}
              />
              <small className="text-muted">{note.length}/500</small>
            </div>

            {/* Inline error */}
            {error && <div className="alert alert-danger py-2 mb-0">{error}</div>}
          </div>

          {/* Footer */}
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="button"
              className={confirmBtnClass}
              onClick={handleSubmit}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" />
                  Processing…
                </>
              ) : (
                confirmBtnLabel
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RefundActionModal;
